import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { SelectionInput, SelectionItem } from "./types";
import { haptics } from "@/lib/haptics";
import { audioBus } from "@/lib/audioBus";

const STORAGE_KEY = "fp.selection.v1";
const HUD_EVENT = "fp:selection:open";

interface SelectionContextValue {
  items: SelectionItem[];
  count: number;
  has: (id: string) => boolean;
  add: (item: SelectionInput) => void;
  remove: (id: string) => void;
  toggle: (item: SelectionInput) => boolean; // returns new saved-state
  clear: () => void;
  /** Imperatively open the HUD. */
  openHud: () => void;
  /** Subscribe component (HUD) to open requests. */
  hudOpenSignal: number;
}

const SelectionContext = createContext<SelectionContextValue | null>(null);

function readStorage(): SelectionItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is SelectionItem =>
        x && typeof x === "object" && typeof x.id === "string" && typeof x.name === "string",
    );
  } catch {
    return [];
  }
}

function writeStorage(items: SelectionItem[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota / private mode — silently ignore */
  }
}

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<SelectionItem[]>([]);
  const [hudOpenSignal, setHudOpenSignal] = useState(0);
  const hydratedRef = useRef(false);

  // Hydrate after mount to avoid SSR/CSR mismatch.
  useEffect(() => {
    setItems(readStorage());
    hydratedRef.current = true;
  }, []);

  // Persist on change (only after hydration).
  useEffect(() => {
    if (!hydratedRef.current) return;
    writeStorage(items);
  }, [items]);

  // Cross-tab sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setItems(readStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Listen for global open events (used by ContactPage preview link, etc.)
  useEffect(() => {
    const onOpen = () => setHudOpenSignal((n) => n + 1);
    window.addEventListener(HUD_EVENT, onOpen);
    return () => window.removeEventListener(HUD_EVENT, onOpen);
  }, []);

  const has = useCallback((id: string) => items.some((i) => i.id === id), [items]);

  // Haptics are intentionally fired from the event handler (not inside the
  // setState updater) so React Strict Mode / future concurrent rendering
  // cannot double-invoke the effect. We compute the action up-front using
  // a functional read, then schedule the state update, then fire once.
  const add = useCallback((input: SelectionInput) => {
    if (!input?.id || !input?.name) return;
    let added = false;
    setItems((prev) => {
      if (prev.some((i) => i.id === input.id)) return prev;
      added = true;
      return [...prev, { ...input, addedAt: Date.now() }];
    });
    if (added) { haptics.success(); audioBus.playSelect(); }
  }, []);

  const remove = useCallback((id: string) => {
    let removed = false;
    setItems((prev) => {
      if (!prev.some((i) => i.id === id)) return prev;
      removed = true;
      return prev.filter((i) => i.id !== id);
    });
    if (removed) haptics.light();
  }, []);

  const toggle = useCallback((input: SelectionInput) => {
    if (!input?.id || !input?.name) return false;
    let nextSaved = false;
    setItems((prev) => {
      const exists = prev.some((i) => i.id === input.id);
      if (exists) {
        nextSaved = false;
        return prev.filter((i) => i.id !== input.id);
      }
      nextSaved = true;
      return [...prev, { ...input, addedAt: Date.now() }];
    });
    if (nextSaved) { haptics.success(); audioBus.playSelect(); }
    else haptics.light();
    return nextSaved;
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const openHud = useCallback(() => setHudOpenSignal((n) => n + 1), []);

  const value = useMemo<SelectionContextValue>(
    () => ({
      items,
      count: items.length,
      has,
      add,
      remove,
      toggle,
      clear,
      openHud,
      hudOpenSignal,
    }),
    [items, has, add, remove, toggle, clear, openHud, hudOpenSignal],
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

export function useSelection(): SelectionContextValue {
  const ctx = useContext(SelectionContext);
  if (!ctx) {
    throw new Error("useSelection must be used inside <SelectionProvider>");
  }
  return ctx;
}

/** Fire from anywhere (e.g. plain links) to open the HUD. */
export function requestOpenSelectionHud() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(HUD_EVENT));
}
